import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../config/api';
import toast from 'react-hot-toast';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'agent' | 'sales' | 'sales_agent' | 'developer';
  phone?: string;
  extension?: string;
  ringcentral_extension?: string;
  is_active: boolean;
  last_login?: string;
  created_at: string;
  updated_at: string;
  avatar_url?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip auth check if we're on the TV Dashboard route (it has its own auth)
    const path = window.location.pathname;
    if (path === '/tvdashboard' || path === '/tv-dashboard' || path.startsWith('/tvdashboard') || path.startsWith('/tv-dashboard')) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      console.log('Auth /me URL:', (response as any)?.request?.responseURL || 'n/a');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      console.log('Auth /login URL:', (response as any)?.request?.responseURL || 'n/a');
      const { token, user: userData } = response.data;
      
      localStorage.setItem('token', token);
      setUser(userData);
      
      toast.success(`Welcome back, ${userData.first_name}!`);
    } catch (error: any) {
      // Don't show toast here - let the Login component handle it
      // Just throw the error so it can be caught and displayed properly
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    toast.success('Logged out successfully');
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
